import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Avatar } from '../../components/ui/Avatar'
import { TableWrap } from '../../components/ui/Table'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { usersApi } from '../../api/users.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import type { User } from '../../api/types'
import { UserFormModal } from './UserFormModal'
import { ResetPasswordModal } from './ResetPasswordModal'

type PendingAction = { type: 'deactivate' | 'reactivate' | 'delete'; user: User }

export function UsersPage() {
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [formUser, setFormUser] = useState<User | null | undefined>(undefined)
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  const usersQuery = useQuery({
    queryKey: ['users', { search, page }],
    queryFn: () => usersApi.list({ search: search || undefined, page, pageSize: 20, includeInactive: true }),
  })

  const actionMutation = useMutation({
    mutationFn: async (action: PendingAction) => {
      if (action.type === 'deactivate') return usersApi.deactivate(action.user.id)
      if (action.type === 'reactivate') return usersApi.reactivate(action.user.id)
      return usersApi.remove(action.user.id)
    },
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      const messages = { deactivate: 'Benutzer deaktiviert.', reactivate: 'Benutzer reaktiviert.', delete: 'Benutzer gelöscht.' }
      toast.success(messages[action.type])
      setPendingAction(null)
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.')
      setPendingAction(null)
    },
  })

  const users = usersQuery.data?.data ?? []
  const total = usersQuery.data?.meta.total ?? 0

  return (
    <div>
      <PageHeader
        title="Benutzer"
        subtitle={`${total} Benutzer im System`}
        actions={
          <PermissionGate anyOf={['users.manage']}>
            <Button variant="primary" onClick={() => setFormUser(null)}>
              + Neuer Benutzer
            </Button>
          </PermissionGate>
        }
      />

      <div style={{ marginBottom: 'var(--space-4)', maxWidth: 320 }}>
        <Input
          placeholder="Suchen nach Name, Benutzername, E-Mail…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
      </div>

      {users.length === 0 && !usersQuery.isLoading ? (
        <EmptyState mascot title="Keine Benutzer gefunden" description="Passe die Suche an oder lege einen neuen Benutzer an." />
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Rollen</th>
                <th>Status</th>
                <th>Letzter Login</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <Avatar name={user.displayName} imageUrl={user.avatarUrl} status={user.status} />
                      <div>
                        <div style={{ fontWeight: 600 }}>{user.displayName}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {user.roles.map((r) => (
                        <Badge key={r.role.id} variant="accent">
                          {r.role.name}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Badge variant={user.isActive ? 'success' : 'neutral'} dot>
                      {user.isActive ? 'Aktiv' : 'Deaktiviert'}
                    </Badge>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('de-DE') : 'Nie'}
                  </td>
                  <td>
                    <PermissionGate anyOf={['users.manage']}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <Button size="sm" variant="ghost" onClick={() => setFormUser(user)}>
                          Bearbeiten
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setResetPasswordUserId(user.id)}>
                          Passwort
                        </Button>
                        {user.isActive ? (
                          <Button size="sm" variant="ghost" onClick={() => setPendingAction({ type: 'deactivate', user })}>
                            Deaktivieren
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setPendingAction({ type: 'reactivate', user })}>
                            Reaktivieren
                          </Button>
                        )}
                        {user.id !== me?.user.id ? (
                          <Button size="sm" variant="danger" onClick={() => setPendingAction({ type: 'delete', user })}>
                            Löschen
                          </Button>
                        ) : null}
                      </div>
                    </PermissionGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {total > 20 ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Zurück
          </Button>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', alignSelf: 'center' }}>
            Seite {page} von {Math.ceil(total / 20)}
          </span>
          <Button size="sm" variant="ghost" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>
            Weiter
          </Button>
        </div>
      ) : null}

      {formUser !== undefined ? <UserFormModal user={formUser} onClose={() => setFormUser(undefined)} /> : null}
      {resetPasswordUserId ? (
        <ResetPasswordModal userId={resetPasswordUserId} onClose={() => setResetPasswordUserId(null)} />
      ) : null}
      {pendingAction ? (
        <ConfirmDialog
          title={
            pendingAction.type === 'delete'
              ? 'Benutzer löschen'
              : pendingAction.type === 'deactivate'
                ? 'Benutzer deaktivieren'
                : 'Benutzer reaktivieren'
          }
          message={`"${pendingAction.user.displayName}" wirklich ${
            pendingAction.type === 'delete' ? 'löschen' : pendingAction.type === 'deactivate' ? 'deaktivieren' : 'reaktivieren'
          }?`}
          danger={pendingAction.type === 'delete'}
          loading={actionMutation.isPending}
          onConfirm={() => actionMutation.mutate(pendingAction)}
          onCancel={() => setPendingAction(null)}
        />
      ) : null}
    </div>
  )
}
